import { useMemo, useState } from 'react';
import { AnalysisTab, AnalyzeCard } from './AnalyzeViews';
import { ScoreboardScreen } from './ScoreboardScreen';
import type { ScoreApi } from './scoreboard';
import type { ScoreV3Api } from './scoreboardV3Api';
import { ChangeLogTab, TrendsTab } from './TrendsAndLog';

export type ScoreTab = 'board' | 'trends' | 'log' | 'analysis';
const TABS: [ScoreTab, string][] = [
  ['board', 'Board'],
  ['trends', 'Trends'],
  ['log', 'Change log'],
  ['analysis', 'Analysis'],
];

/** v2's Board stays exactly as it was; v3 adds three tabs and the Analyze card above it. */
export function ScoreboardTabs({
  api,
  v3,
  cacheScope,
  initialTab = 'board',
}: {
  api: ScoreApi;
  v3: ScoreV3Api;
  cacheScope: string;
  initialTab?: ScoreTab;
}) {
  const [tab, setTab] = useState<ScoreTab>(initialTab);
  const [metric, setMetric] = useState('');
  const [toolsHost, setToolsHost] = useState<HTMLDivElement | null>(null);
  const board = useMemo(
    () => (
      <ScoreboardScreen
        api={api}
        cacheScope={cacheScope}
        days={v3.days}
        toolsHost={toolsHost}
        aboveTiles={<AnalyzeCard api={v3} onRead={() => setTab('analysis')} />}
      />
    ),
    [api, v3, cacheScope, toolsHost],
  );
  return (
    <>
      <div className="score-tabs-row">
        <div className="tracker-tab-nav" role="group" aria-label="Scoreboard views">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`tracker-tab-button${tab === key ? ' tracker-tab-button--active' : ''}`}
              aria-pressed={tab === key}
              onClick={() => {
                setMetric('');
                setTab(key);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="score-tab-tools" ref={setToolsHost} />
      </div>
      <div className="scoreboard">
        {tab === 'board' ? board : null}
        {tab === 'trends' ? (
          <TrendsTab
            api={v3}
            onBoard={() => setTab('board')}
            onChanges={(id) => {
              setMetric(id);
              setTab('log');
            }}
          />
        ) : null}
        {tab === 'log' ? (
          <ChangeLogTab api={v3} metric={metric} onClearMetric={() => setMetric('')} />
        ) : null}
        {tab === 'analysis' ? (
          <AnalysisTab api={v3} onChangeLog={() => setTab('log')} onBoard={() => setTab('board')} />
        ) : null}
      </div>
    </>
  );
}
